<template>
    <div>
        <InvoicePaymentDetails v-if="isInvoicePayment"></InvoicePaymentDetails>
        <div class="row my-4">
            <div class="col-12">
                <h1 class="text-3xl ps-2">
                    <strong v-if="!isDummyVipps">Vipps</strong>{{ $t('betaling') }}<span v-if="isDummyVipps"> - {{ $t('dummy Vipps') }}</span>
                </h1>
            </div>
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <div v-if="!isInvoicePayment" class="form-group row justify-content-between align-bottom">
                            <div class="col-md-8 col-6">
                                <label class="mt-0 my-md-0">{{ amountLabel }}:</label>
                            </div>
                            <div class="col-6 col-lg-3 col-md-4">
                                <div v-if="isInvoicePayment || payInvoiceableLines" class="text-end">{{ formattedBalanceAmount }}</div>
                                <input v-else type="text" disabled :value="formattedBalanceAmount" class="form-control text-end">
                            </div>
                        </div>
                        <div class="form-group row justify-content-between align-bottom">
                            <div class="col-md-8 col-6">
                                <label class="mt-0 my-md-0">{{ $t('Beløp å betale') }}:</label>
                            </div>
                            <div class="col-6 col-lg-3 col-md-4">
                                <div v-if="isInvoicePayment || payInvoiceableLines" class="text-end">{{ formattedSum }}</div>
                                <input v-else-if="!payInvoiceableLines" type="number" :value="sum" @keypress="validateValue" @change="setSum" @keyup="setSum" @blur="setSum" min="0" :max="maxSum" :disabled="isLoading" class="form-control text-end">
                            </div>
                        </div>
                        <div class="form-group row justify-content-between align-bottom mb-4" v-show="transactionFee > 0">
                            <div class="col-md-8 col-6">
                                <label class="mt-0 my-md-0">{{ $t('Straksbetaling') }}:</label>
                            </div>
                            <div class="col-6 col-lg-3 col-md-4 text-end">
                                {{transactionFeeFormatted}}%
                            </div>
                        </div>
                        <div class="form-group row justify-content-between align-bottom" v-show="transactionFee > 0">
                            <div class="col-md-8 col-6">
                                <label class="mt-0 my-md-0">{{ $t('Totalsum til {officeName}', {officeName}) }}:</label>
                            </div>
                            <div class="col-6 col-lg-3 col-md-4">
                                <div v-if="isInvoicePayment || payInvoiceableLines" class="text-end">{{ formattedSumIncludingFee }}</div>
                                <input v-else type="text" disabled :value="sumIncludingFee" class="form-control text-end">
                            </div>
                        </div>
                        <div v-if="!isApp" class="form-group row justify-content-between align-bottom">
                            <div class="col-md-8 col-6">
                                <label class="mt-0 my-md-0">{{ $t('Betal fra mobilnummer') }}:</label>
                            </div>
                            <div class="col-6 col-lg-3 col-md-4">
                                <select class="form-select" @change="setVippsMobile">
                                    <option :value="mobile" v-for="mobile in mobileNumbers" class="text-end">{{ mobile }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-sm-12">
                            <div class="alert alert-info">
                                <p class="m-0"><strong>{{ $t('Til informasjon') }}:</strong> {{ $t('Vipps-betalingen må godkjennes innen fem minutter, ellers blir forespørselen kansellert av Vipps.') }}
                                    <br>
                                    {{ $t('Betalingen gjøres med befriende virkning til TABS AS, som mottar betalingen på vegne av og etter avtale med trafikkskolen. Din betaling registreres umiddelbart på din elevkonto hos {officeName}', { officeName: officeName }) }}.</p>
                            </div>
                        </div>
                        <div class="col-sm-12">
                            <div class="d-flex flex-column flex-md-row justify-content-end gap-2">
                                <button class="btn btn-vipps btn-block btn-inline-block-md fw-bold" @click.prevent="submitVipps" :disabled="submitDisabled">
                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-show="isLoading"></span>
                                    {{ $t('Send Vipps!') }}
                                </button>
                                <button
                                    v-if="showDummyVippsButton"
                                    class="btn btn-secondary btn-block btn-inline-block-md fw-bold"
                                    @click.prevent="submitDummyVipps"
                                    :disabled="submitDisabled"
                                >
                                    {{ $t('Betal med dummy Vipps') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-if="payInvoiceableLines && notInvoicedLines.length" class="mb-4">
                        <h2 class="text-xl ps-2">{{ $t('Ufakturerte varelinjer') }}</h2>
                        <NotInvoicedLines
                            :sales="notInvoicedLines"
                            :selectable="true"
                            :selected-sale-ids="selectedNotInvoicedLineIds"
                            @toggle-selection="toggleNotInvoicedLine"
                        ></NotInvoicedLines>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
    select {text-align-last: right;}
    option {direction: rtl;}
    label {
        word-break: normal;
        hyphens: auto;
    }
</style>
<script setup>
import { computed, watch } from 'vue'
import Store from '../../state/paymentStore'
import VippsStore from '../../state/vippsStore'
import { toMoney } from "../../lib/format"
import InvoicePaymentDetails from './InvoicePaymentDetails'
import NotInvoicedLines from './NotInvoicedLines'

const props = defineProps({
    showDummyVippsButton: {
        type: Boolean,
        default: false,
    },
})

const isInvoicePayment = computed(() => Boolean(Store.getAccountOrderHistoryId()))
const isDummyVipps = computed(() => Store.getPaymentMethod() === 'dummyVipps')
const showDummyVippsButton = computed(() => props.showDummyVippsButton && Store.getPaymentMethods().dummyVipps)
const amountLabel = computed(() => Store.getBalanceAmountLabel())
const balanceAmount = computed(() => Store.getBalanceAmount())
const formattedBalanceAmount = computed(() => toMoney(balanceAmount.value))
const transactionFee = computed(() => Store.getTransactionFee())
const transactionFeeFormatted = computed(() => Store.getTransactionFee().toString().replace('.', ','))
const mobileNumbers = computed(() => Store.getMobileNumbers())
const sum = computed(() => Store.getSum())
const formattedSum = computed(() => toMoney(sum.value))
const maxSum = computed(() => Store.getMaxSum())
const sumIncludingFee = computed(() => Store.getSumIncludingFee())
const formattedSumIncludingFee = computed(() => toMoney(sumIncludingFee.value))
const isLoading = computed(() => Store.getLoading())
const payInvoiceableLines = computed(() => Store.payInvoiceableLines())
const notInvoicedLines = computed(() => Store.getNotInvoicedLines())
const selectedNotInvoicedLineIds = computed(() => Store.getSelectedNotInvoicedLineIds())
const vippsRequestInProgress = computed(() => VippsStore.getIsActive())
const officeName = computed(() => Store.getOfficeName())
const isApp = computed(() => window.isApp)
const submitDisabled = computed(() => isLoading.value || (payInvoiceableLines.value && selectedNotInvoicedLineIds.value.length === 0))

const validateValue = (event) => {
    if ([',','.','-'].indexOf(event.key) !== -1) {
        event.preventDefault()
    }
}

const setSum = (event) => {
    if (parseInt(event.target.value) > maxSum.value) {
        event.target.value = maxSum.value
    }
    Store.setSum(event.target.value)
}

const setVippsMobile = (event) => {
    Store.setVippsMobile(event.target.value)
}

const toggleNotInvoicedLine = (saleId) => {
    Store.toggleNotInvoicedLine(saleId)
}

const submitVipps = () => {
    Store.setPaymentMethod('vipps')
    Store.submitVipps()
}

const submitDummyVipps = () => {
    Store.setPaymentMethod('dummyVipps')
    Store.submitVipps()
}

watch(vippsRequestInProgress, (isActive) => {
    if (!isActive) {
        Store.setDisplayPaymentMethods(true)
        Store.setPaymentMethod('vipps')
    }
})
</script>
